import {
	Component, EventEmitter,
	Inject,
	Input,
	OnDestroy,
	OnInit, Output, ViewChild
} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MAT_MOMENT_DATE_FORMATS,
	MomentDateAdapter
} from '@angular/material-moment-adapter';
import { TranslateService } from '@ngx-translate/core';
import {
	DateRange,
	DefaultMatCalendarRangeStrategy,
	MAT_DATE_RANGE_SELECTION_STRATEGY,
	MatCalendar
} from '@angular/material/datepicker';
import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { tinykeys } from 'tinykeys';
import moment from 'moment';

// models
import { DEFAULTS_PRESETS, DEFAULTS_RANGE_PRESETS, IPresetInput, Preset } from './preset';

// components
import { CustomHeader } from './custom-header/custom-header.component';

// services
import { SessionService } from '../../../services/session.service';
import { Utils } from '../../../services/utils.service';

import { SHARED_MODULES } from '../../../../shared.';
import { OverlayModule } from '@angular/cdk/overlay';
import { PhButtonComponent } from '../../ph-button/ph-button.component';
import { PhIconComponent } from '../../ph-icon/ph-icon.component';

const MAX_DATE: string = '2050-12-31',
	FORMATS: Array<string> = ['ddd MMM DD YYYY', 'YYYY-MM-DD'];

@Component({
    selector: 'ph-datepicker',
    templateUrl: 'ph-datepicker.component.html',
    styleUrls: ['ph-datepicker.component.scss'],
	imports: [SHARED_MODULES, OverlayModule, PhButtonComponent, PhIconComponent],
    providers: [
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'us'
        },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: MAT_MOMENT_DATE_FORMATS
        },
        {
            provide: MatCalendar
        },
        {
            provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
            useClass: DefaultMatCalendarRangeStrategy,
        },
    ],
    standalone: true
})
export class PhDatepickerComponent implements OnInit, OnDestroy {

	customHeader = CustomHeader;
	opened: boolean = false;
	selectedDate: moment.Moment | DateRange<moment.Moment> = null;
	confirmedDate: moment.Moment | DateRange<moment.Moment> = null;
	min: moment.Moment | null;
	max: moment.Moment | null;
	presets: Array<Preset> = [];
	previousActivePreset: Preset;
	tinyKeysSubscription: () => void;

	overlayPositions: ConnectionPositionPair[] = [
		// Bottom position (primary)
		{
			originX: 'center',
			originY: 'bottom',
			overlayX: 'center',
			overlayY: 'top',
			offsetY: 8
		},
		// Top position (fallback)
		{
			originX: 'center',
			originY: 'top',
			overlayX: 'center',
			overlayY: 'bottom',
			offsetY: -8
		},
		// Right position (fallback)
		{
			originX: 'end',
			originY: 'center',
			overlayX: 'start',
			overlayY: 'center',
			offsetX: 8
		},
		// Left position (fallback)
		{
			originX: 'start',
			originY: 'center',
			overlayX: 'end',
			overlayY: 'center',
			offsetX: -8
		},
		// Bottom-right (fallback)
		{
			originX: 'end',
			originY: 'bottom',
			overlayX: 'end',
			overlayY: 'top',
			offsetY: 8
		},
		// Bottom-left (fallback)
		{
			originX: 'start',
			originY: 'bottom',
			overlayX: 'start',
			overlayY: 'top',
			offsetY: 8
		}
	];

	@Input('presets') set setPresets(value: Array<IPresetInput>) {
		// not working defaultDateTo (need for range)
		if (!value) return;
		this.presets = value.map((preset: IPresetInput): Preset => new Preset(preset));
	}
	@Input('maxDate')
	set setMaxDate(value: string) {
		if (value) {
			this.max = moment(value.substring(0, 15), FORMATS);
		} else {
			this.max = moment(
				this.isRange
					? this.sessionService.defaults.date_to
					: MAX_DATE,
				FORMATS
			);
		}
		this.disableInvalidPresets();
	}
	@Input('minDate')
	set setMinDate(value: string) {
		if (value) {
			this.min = moment(value.substring(0, 15), FORMATS);
		} else {
			if (this.isRange) {
				this.min = moment(this.sessionService.defaults.date_from, FORMATS);
			} else {
				this.min = null;
			}
		}
		this.disableInvalidPresets();
	}
	@Input('selectedDate') set setSelectedDate(value: string | DateRange<string>) {
		if (!value) return;
		if (this.isRange && !(value as DateRange<string>).start) return;
		this.setDates(value);
	}
	@Input() error: 'lessToday' | 'lessDate';
	@Input() tooltip: string;
	@Input() lessDate: string;
	@Input() displayIconMode: boolean = false;
	@Input('range') isRange: boolean = false;
	@Input('default') defaultDate: string | DateRange<string> | DateRange<Date>;
	@Input('label') label: string;
	@Input('placeholder') placeholder: string;

	@Output() setDate: EventEmitter<Date | DateRange<Date> | null> = new EventEmitter<Date | DateRange<Date> | null>();

	@ViewChild('calendar') calendar: MatCalendar<any>;

	constructor(
		private _adapter: DateAdapter<any>,
		private translate: TranslateService,
		private sessionService: SessionService,
		@Inject(MAT_DATE_LOCALE) private readonly _locale: string
	) {
		this._locale = this.translate.currentLang;
		this._adapter.setLocale(this._locale);

		this.tinyKeysSubscription = tinykeys(window, {
			'Delete': () => {
				if (!this.opened) return;
				return this.cancelPicker();
			}
		});
	}

	get getError(): boolean {
		if (!this.error || this.confirmedDate instanceof DateRange) return;

		switch (this.error) {
			case 'lessToday': return this.confirmedDate && Utils.dateDayComparison(new Date(), this.confirmedDate.toDate());
			case 'lessDate': return this.lessDate && this.confirmedDate && Utils.dateDayComparison(new Date(this.lessDate), this.confirmedDate.toDate());
		}
	}

	ngOnInit(): void {
		if (!this.presets.length)
			(this.isRange ? DEFAULTS_RANGE_PRESETS : DEFAULTS_PRESETS).forEach((preset: IPresetInput) => {
				if (this.isRange)
					preset.defaultDate = new DateRange<Date>(
						new Date(this.min?.toDate() || this.sessionService.defaults.date_from),
						new Date(this.max?.toDate() || this.sessionService.defaults.date_to)
					);
				this.presets.push(new Preset(preset));
			});
		if (!this.max) this.max = moment(this.isRange ? this.sessionService.defaults.date_to : MAX_DATE, FORMATS);
		if (this.isRange) {
			this.min = moment(this.sessionService.defaults.date_from, FORMATS);
		}
		if (!this.selectedDate) this.setDates(this.defaultDate);
		this.disableInvalidPresets();
	}

	setDates(value: string | Date | DateRange<string> | DateRange<Date>): void {
		if (!value) return;
		if (value instanceof Date) value = value.toString();
		if (typeof value === 'string') {
			this.selectedDate = this.confirmedDate = moment(value.substring(0, 15), FORMATS);
			if (value === this.defaultDate) {
				this.presets.forEach((preset: Preset) => preset.active = false);
			}
		} else {
			const start: any = (value as any).start;
			const end: any = (value as any).end ?? (value as any).start;
			if (!start) return;
			this.selectedDate = this.confirmedDate = new DateRange<moment.Moment>(
				moment(start, FORMATS),
				moment(end, FORMATS)
			);
			const def: any = this.defaultDate as any;
			if (
				def && def.start !== undefined && def.end !== undefined &&
				value.start === def.start &&
				value.end === def.end
			) {
				this.presets.forEach((preset: Preset) => preset.active = false);
			}
		}
	}

	disableInvalidPresets(): void {
		if (!this.presets?.length) return;
		const minDate = this.min?.startOf('day');
		const maxDate = this.max?.startOf('day');

		this.presets.forEach(preset => {
			if (preset.type === 'now') {
				preset.disabled = false;
				return;
			}

			const presetDate = preset.date instanceof DateRange
				? preset.date.start?.startOf('day')
				: (preset.date as moment.Moment)?.startOf('day');

			if (!presetDate) {
				preset.disabled = true;
				return;
			}

			if (minDate && presetDate.isBefore(minDate)) {
				preset.disabled = true;
				return;
			}
			if (maxDate && presetDate.isAfter(maxDate)) {
				preset.disabled = true;
				return;
			}
			preset.disabled = false;
		});
	}

	openPicker(): void {
		this.opened = true;
		this.previousActivePreset = this.presets.find((preset: Preset) => preset.active);
	}

	selectedChange(date: moment.Moment): void {
		this.presets.forEach((preset: Preset) => preset.active = false);
		if (this.isRange) {
            const selectedDate = this.selectedDate as DateRange<moment.Moment>;
			if (selectedDate && selectedDate.start && !selectedDate.end) {
				this.selectedDate = date > selectedDate.start
					? new DateRange(selectedDate.start, date)
					: new DateRange(date, selectedDate.start);
			} else {
				this.selectedDate = new DateRange(date, null);
			}
		} else {	
			this.selectedDate = date;
		}
	}

	closePicker(simpleClose: boolean = false): void {
		if (!this.opened) return;
		this.selectedDate = this.confirmedDate;
		this.opened = false;
		if (simpleClose) {
			this.presets.forEach((preset: Preset) => preset.active = preset === this.previousActivePreset)
		}
	}

	setPresetDate(preset: Preset): void {
		this.selectedDate = preset.date;
		if (this.selectedDate instanceof DateRange) {
			const dateTo = this.selectedDate.end,
				dateFrom = this.selectedDate.start;
			dateTo.locale(this._locale);
			dateFrom.locale(this._locale);
			this.calendar.activeDate = this.selectedDate.start;
		} else {
			const date = moment(this.selectedDate as moment.Moment, FORMATS);
			date.locale(this._locale);
			this.calendar.activeDate = date;
		}
		this.calendar.selected = this.selectedDate;
		this.presets.forEach((preset: Preset) => preset.active = false);
		preset.active = true;
	}

	cancelPicker(): void {
		if (this.defaultDate) this.setDates(this.defaultDate);
		else this.confirmedDate = null;
		let result: Date | DateRange<Date>;
		if (this.confirmedDate instanceof DateRange) result = new DateRange(this.confirmedDate.start.toDate(), this.confirmedDate.end.toDate());
		else result = this.confirmedDate?.toDate();
		this.setDate.emit(result);
		this.closePicker();
	}

	applyPicker(): void {
		if ((this.selectedDate instanceof DateRange) ? !this.selectedDate?.start : !this.selectedDate) return;
		if (this.selectedDate instanceof DateRange && !this.selectedDate.end) {
			this.selectedDate = new DateRange<moment.Moment>(
				this.selectedDate.start,
				this.selectedDate.start
			)
		}
		if (this.confirmedDate != this.selectedDate) {
			this.confirmedDate = this.selectedDate;
			let result: Date | DateRange<Date>;
			if (this.confirmedDate instanceof DateRange) {
				result = new DateRange(this.confirmedDate.start.toDate(), this.confirmedDate.end.toDate());
			}
			else {
				result = this.confirmedDate?.toDate();
			}
			this.setDate.emit(result);
		}
		this.closePicker();
	}

	ngOnDestroy() {
		if (this.tinyKeysSubscription) this.tinyKeysSubscription();
	}
}
